/**
 * IPv4/IPv6 address and CIDR math.
 *
 * Addresses are represented as `bigint` so v4 and v6 share one code path. All
 * parsing is strict: anything this module cannot parse unambiguously returns
 * `null` rather than guessing, because the callers (address allocation, WireGuard
 * validation) use these results to decide whether two tenants' address space can
 * overlap.
 */

export type IpFamily = 4 | 6;

export interface IpAddress {
  family: IpFamily;
  value: bigint;
}

export interface IpCidr {
  family: IpFamily;
  /** Network address (host bits already cleared). */
  base: bigint;
  prefix: number;
}

const V4_BITS = 32;
const V6_BITS = 128;

export const familyBits = (family: IpFamily): number => (family === 4 ? V4_BITS : V6_BITS);

/** Strict IPv4 parse: exactly four decimal octets, no leading zeros, 0–255. */
function parseIpv4(input: string): bigint | null {
  const parts = input.split('.');
  if (parts.length !== 4) return null;
  let value = 0n;
  for (const part of parts) {
    if (!/^\d{1,3}$/.test(part)) return null;
    if (part.length > 1 && part.startsWith('0')) return null; // 010 is ambiguous
    const octet = Number(part);
    if (octet > 255) return null;
    value = (value << 8n) | BigInt(octet);
  }
  return value;
}

/** Strict IPv6 parse, including `::` compression and a trailing embedded IPv4. */
function parseIpv6(input: string): bigint | null {
  if (input.includes('%')) return null; // zone IDs are not routable config
  const doubleColonCount = input.split('::').length - 1;
  if (doubleColonCount > 1) return null;

  let head = input;
  let tail = '';
  if (doubleColonCount === 1) {
    const [h, t] = input.split('::');
    head = h ?? '';
    tail = t ?? '';
  }

  const toGroups = (segment: string): string[] | null => {
    if (segment === '') return [];
    const groups: string[] = [];
    for (const raw of segment.split(':')) {
      if (raw === '') return null;
      if (raw.includes('.')) {
        // Embedded IPv4 — only legal as the final element.
        const v4 = parseIpv4(raw);
        if (v4 === null) return null;
        groups.push(((v4 >> 16n) & 0xffffn).toString(16), (v4 & 0xffffn).toString(16));
        continue;
      }
      if (!/^[0-9a-fA-F]{1,4}$/.test(raw)) return null;
      groups.push(raw);
    }
    return groups;
  };

  const headGroups = toGroups(head);
  const tailGroups = toGroups(tail);
  if (headGroups === null || tailGroups === null) return null;

  // An embedded IPv4 must be last; toGroups expanded it into two groups, so the
  // only structural check left is the total group count.
  const total = headGroups.length + tailGroups.length;
  if (doubleColonCount === 1) {
    if (total > 7) return null; // "::" must stand for at least one zero group
  } else if (total !== 8) {
    return null;
  }

  const zeros = new Array<string>(8 - total).fill('0');
  const groups = [...headGroups, ...zeros, ...tailGroups];

  let value = 0n;
  for (const group of groups) {
    value = (value << 16n) | BigInt(parseInt(group, 16));
  }
  return value;
}

/** Parse an IPv4 or IPv6 address. Returns null if it is not a valid literal. */
export function parseIp(input: string): IpAddress | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  if (trimmed.includes(':')) {
    const value = parseIpv6(trimmed);
    return value === null ? null : { family: 6, value };
  }
  const value = parseIpv4(trimmed);
  return value === null ? null : { family: 4, value };
}

function formatIpv4(value: bigint): string {
  return [24n, 16n, 8n, 0n].map((shift) => Number((value >> shift) & 0xffn)).join('.');
}

/** Canonical IPv6 text per RFC 5952: lowercase, longest zero-run compressed. */
function formatIpv6(value: bigint): string {
  const groups: number[] = [];
  for (let i = 7n; i >= 0n; i -= 1n) {
    groups.push(Number((value >> (i * 16n)) & 0xffffn));
  }

  let bestStart = -1;
  let bestLen = 0;
  let runStart = -1;
  for (let i = 0; i <= groups.length; i += 1) {
    if (i < groups.length && groups[i] === 0) {
      if (runStart === -1) runStart = i;
    } else if (runStart !== -1) {
      const len = i - runStart;
      // Strictly greater keeps the leftmost run on a tie, as the RFC requires.
      if (len > bestLen) {
        bestLen = len;
        bestStart = runStart;
      }
      runStart = -1;
    }
  }

  const hex = groups.map((g) => g.toString(16));
  if (bestLen < 2) return hex.join(':');

  const head = hex.slice(0, bestStart).join(':');
  const tail = hex.slice(bestStart + bestLen).join(':');
  return `${head}::${tail}`;
}

/** Render an address back to canonical text. */
export function formatIp(family: IpFamily, value: bigint): string {
  return family === 4 ? formatIpv4(value) : formatIpv6(value);
}

const maskFor = (family: IpFamily, prefix: number): bigint => {
  const bits = familyBits(family);
  if (prefix === 0) return 0n;
  return ((1n << BigInt(prefix)) - 1n) << BigInt(bits - prefix);
};

/**
 * Parse a CIDR. Host bits are cleared, so `10.0.0.5/24` parses to the `10.0.0.0/24`
 * network — callers that care about the host part should parse the address instead.
 */
export function parseCidr(input: string): IpCidr | null {
  const trimmed = input.trim();
  const slash = trimmed.lastIndexOf('/');
  if (slash === -1) return null;

  const addr = parseIp(trimmed.slice(0, slash));
  if (!addr) return null;

  const prefixText = trimmed.slice(slash + 1);
  if (!/^\d{1,3}$/.test(prefixText)) return null;
  const prefix = Number(prefixText);
  if (prefix > familyBits(addr.family)) return null;

  return { family: addr.family, base: addr.value & maskFor(addr.family, prefix), prefix };
}

/** Canonical `network/prefix` text, or null if the input is not a valid CIDR. */
export function canonicalizeCidr(input: string): string | null {
  const cidr = parseCidr(input);
  return cidr ? formatCidr(cidr) : null;
}

export function formatCidr(cidr: IpCidr): string {
  return `${formatIp(cidr.family, cidr.base)}/${cidr.prefix}`;
}

/** Last address inside a CIDR (broadcast address for v4). */
export function cidrLast(cidr: IpCidr): bigint {
  const bits = familyBits(cidr.family);
  return cidr.base | ((1n << BigInt(bits - cidr.prefix)) - 1n);
}

/** Total addresses a CIDR spans. */
export function cidrSize(cidr: IpCidr): bigint {
  return 1n << BigInt(familyBits(cidr.family) - cidr.prefix);
}

/** True when `inner` is entirely inside `outer` (same family, not shorter prefix). */
export function cidrContains(outer: IpCidr, inner: IpCidr): boolean {
  if (outer.family !== inner.family) return false;
  if (inner.prefix < outer.prefix) return false;
  return (inner.base & maskFor(outer.family, outer.prefix)) === outer.base;
}

/** True when a CIDR covers a bare address. */
export function cidrContainsIp(cidr: IpCidr, addr: IpAddress): boolean {
  if (cidr.family !== addr.family) return false;
  return (addr.value & maskFor(cidr.family, cidr.prefix)) === cidr.base;
}

/**
 * True when two CIDRs share any address. Two ranges of the same family overlap
 * exactly when one contains the other, so this is symmetric containment.
 */
export function cidrsOverlap(a: IpCidr, b: IpCidr): boolean {
  if (a.family !== b.family) return false;
  return cidrContains(a, b) || cidrContains(b, a);
}

/** The nth address inside a CIDR, or null if it falls outside the range. */
export function nthAddress(cidr: IpCidr, index: bigint): bigint | null {
  if (index < 0n || index >= cidrSize(cidr)) return null;
  return cidr.base + index;
}

/**
 * The nth same-sized subnet of `prefix` length carved out of `cidr`. Used to hand
 * out point-to-point links (a /31 per link out of a /24, say).
 */
export function nthSubnet(cidr: IpCidr, prefix: number, index: bigint): IpCidr | null {
  if (prefix < cidr.prefix || prefix > familyBits(cidr.family)) return null;
  const count = 1n << BigInt(prefix - cidr.prefix);
  if (index < 0n || index >= count) return null;
  const step = 1n << BigInt(familyBits(cidr.family) - prefix);
  return { family: cidr.family, base: cidr.base + index * step, prefix };
}

/** True if the CIDR is a default route (`0.0.0.0/0` or `::/0`). */
export function isDefaultRoute(cidr: IpCidr): boolean {
  return cidr.prefix === 0;
}

/**
 * Normalize a source address for allowlist matching. An IPv4-mapped IPv6 address
 * (`::ffff:127.0.0.1`) — the form a dual-stack socket reports for an IPv4 client —
 * is reduced to its IPv4 form, and a zone id (`fe80::1%eth0`) is dropped, so a rule
 * written for the plain address matches regardless of how the socket presented it.
 */
export function normalizeClientIp(ip: string): string {
  const zoneless = ip.split('%')[0] ?? ip;
  const mapped = /^::ffff:(\d+\.\d+\.\d+\.\d+)$/i.exec(zoneless);
  return mapped ? (mapped[1] as string) : zoneless;
}

/**
 * Whether a client address is permitted by an allowlist of CIDRs. An empty list
 * means unrestricted (allow). A source that will not parse is denied when the list
 * is non-empty — fail closed — and an unparseable rule is skipped rather than
 * matching everything.
 */
export function ipAllowedByCidrs(ip: string, cidrs: string[]): boolean {
  if (cidrs.length === 0) return true;
  const addr = parseIp(normalizeClientIp(ip));
  if (!addr) return false;
  for (const raw of cidrs) {
    const cidr = parseCidr(raw);
    if (cidr && cidrContainsIp(cidr, addr)) return true;
  }
  return false;
}
